import { Icon16KeyOutline, Icon16MoreHorizontal, Icon20KeyOutline } from '@vkontakte/icons';
import { Button } from '@vkontakte/vkui';
import clsx from 'clsx';
import React, { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { IS_REQUIRED_B2B_PASSWORD_PUBLIC } from 'reactApp/appHelpers/configHelpers';
import { isPublicPasswordEnabled } from 'reactApp/appHelpers/featuresHelpers/features/publicPassword';
import { isPublicPasswordFileEnabled } from 'reactApp/appHelpers/featuresHelpers/features/publicPasswordFile';
import { renderPublicPassword } from 'reactApp/components/SharingNewBiz/PublicPasswordDialog/PublicPasswordDialog.helpers';
import { QA_VALUE } from 'reactApp/components/SharingNewBiz/SharingNew.constants';
import { closeSharingNew, renderSharingNew } from 'reactApp/components/SharingNewBiz/SharingNew.helpers';
import { type IProps as SharingNewProps, DomainAccess } from 'reactApp/components/SharingNewBiz/SharingNew.types';
import styles from 'reactApp/components/SharingNewBiz/Weblink/SharingNewWeblink.css';
import b2cStyles from 'reactApp/components/SharingWindow/Weblink/SharingWeblink.css';
import { weblinkSetDomainAccess } from 'reactApp/modules/modifying/modifying.actions';
import type { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import { setPublicPin } from 'reactApp/modules/public/public.actions';
import type { EStorageType } from 'reactApp/modules/storage/storage.types';
import { ButtonLink } from 'reactApp/ui/ButtonLink/ButtonLink';

import { renderDeletePublicPasswordDialog } from './DeletePublicPasswordDialog/DeletePublicPasswordDialog.helpers';
import { Dropdown } from './Dropdown/Dropdown';

export interface Props {
    pin?: string;
    item?: PublishItem;
    formProps: SharingNewProps;
    redesign?: boolean;
    storage: EStorageType;
    isPhone?: boolean;
}

interface WeblinkPublicPasswordControlProps {
    hasPin: boolean;
    redesign?: boolean;
    isPhone?: boolean;
    handleToggleMenu: () => void;
    handleSetPassword: () => void;
    menuRef: React.MutableRefObject<HTMLDivElement | null>;
}

const WeblinkPublicPasswordControl = ({
    hasPin,
    redesign,
    handleToggleMenu,
    handleSetPassword,
    menuRef,
    isPhone,
}: WeblinkPublicPasswordControlProps) => {
    if (hasPin) {
        return redesign ? (
            <div ref={menuRef}>
                <Button
                    data-qa-id="password-settings-menu-button"
                    className={styles.dropdownButton}
                    appearance="accent"
                    mode="tertiary"
                    size="l"
                    onClick={handleToggleMenu}
                    before={<Icon16MoreHorizontal />}
                />
            </div>
        ) : (
            <ButtonLink className={clsx({ [styles.dropdownPhoneLink]: isPhone })} hoverUnderline={false}>
                Установлен
            </ButtonLink>
        );
    }

    return (
        <div className={styles.dropdown}>
            {redesign ? (
                <Button className={styles.dropdownButton} appearance="accent" mode="tertiary" size="l" onClick={handleSetPassword}>
                    установить
                </Button>
            ) : (
                <ButtonLink
                    className={clsx({ [styles.dropdownPhoneLink]: isPhone })}
                    primary
                    hoverUnderline={false}
                    onClick={handleSetPassword}
                >
                    Установить
                </ButtonLink>
            )}
        </div>
    );
};

// eslint-disable-next-line max-lines-per-function
export const WeblinkPublicPassword = ({ pin, isPhone, item, formProps, redesign, storage }: Props) => {
    const dispatch = useDispatch();
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [isShowMenu, setShowMenu] = useState(false);
    const [password, setPassword] = useState<string>('');
    const hasPin = pin === 'true'; // для обратной совместимости текущей ручки реализовано так
    const domainAccess = (item && 'weblinkDomainAccess' in item && item?.weblinkDomainAccess) || DomainAccess.EXTERNAL;

    const handleToggleMenu = useCallback(() => {
        setShowMenu((prev) => !prev);
    }, []);

    const handleRestorePassword = useCallback(
        (oldPassword: string) => {
            if (!item?.weblink) {
                return;
            }

            dispatch(
                setPublicPin({
                    pin: oldPassword,
                    public_id: item.weblink,
                    onSuccess: () => {
                        setPassword(oldPassword);
                    },
                })
            );
        },
        [dispatch, item?.weblink]
    );

    const handleSetPassword = useCallback(() => {
        renderPublicPassword({
            redesign,
            item,
            pin: password,
            onSuccess: () => renderSharingNew(formProps),
            onDialogClose: () => renderSharingNew(formProps),
        });
        closeSharingNew();
    }, [formProps, item, password, redesign]);

    const handleDeletePassword = useCallback(() => {
        if (!item?.weblink) {
            return;
        }

        renderDeletePublicPasswordDialog({
            onDelete: () => {
                if (
                    isPublicPasswordEnabled &&
                    IS_REQUIRED_B2B_PASSWORD_PUBLIC &&
                    (item?.isFolder || isPublicPasswordFileEnabled) &&
                    domainAccess === DomainAccess.EXTERNAL &&
                    redesign
                ) {
                    dispatch(weblinkSetDomainAccess({ domain: DomainAccess.DOMESTIC, itemId: item.id, storage }));
                }
                setPassword('');
            },
            onRestore: () => handleRestorePassword(password),
            onDialogClose: () => renderSharingNew(formProps),
            publicId: item.weblink,
            redesign,
        });
        closeSharingNew();
    }, [item?.weblink, item?.isFolder, item?.id, redesign, domainAccess, dispatch, storage, handleRestorePassword, password, formProps]);

    return (
        <div className={styles.rightsItem} data-qa-item={QA_VALUE.passwordItem}>
            <div className={styles.titleWrapper}>
                {redesign ? (
                    <span className={clsx(b2cStyles.rightTitle, b2cStyles.tabs)}>
                        <Icon20KeyOutline />
                        Пароль для доступа
                    </span>
                ) : (
                    <>
                        <span className={styles.icon}>
                            <Icon16KeyOutline />
                        </span>
                        <span className={styles.rightsTitle}>Пароль для доступа по ссылке</span>
                    </>
                )}
                {hasPin && !redesign && (
                    <div
                        data-qa-id="password-settings-menu-button"
                        className={clsx(styles.menuIcon, { [styles.opened]: isShowMenu })}
                        ref={menuRef}
                        onClick={handleToggleMenu}
                    >
                        <Icon16MoreHorizontal />
                    </div>
                )}
                {isShowMenu && (
                    <Dropdown
                        ref={menuRef}
                        isExternalAccess={!item?.weblinkDomestic}
                        onClose={handleToggleMenu}
                        onDeletePassword={handleDeletePassword}
                        onChangePassword={handleSetPassword}
                        redesign={redesign}
                    />
                )}
            </div>
            <WeblinkPublicPasswordControl
                handleSetPassword={handleSetPassword}
                handleToggleMenu={handleToggleMenu}
                hasPin={hasPin}
                redesign={redesign}
                menuRef={menuRef}
                isPhone={isPhone}
            />
        </div>
    );
};
